import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Banner, Button, Card, Checkbox, FormLayout, IndexTable, Select, Tabs, Text, TextField } from "@shopify/polaris";
import { useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { QuestionCircleIcon } from "@shopify/polaris-icons";
import agent from "../../app/api/agent";
import { AdminDataFeedInventoryDiagnosticsResponse, AdminDataFeedProfile, AdminDataFeedShopifyLocation, AdminDataFeedStoreDetails, AdminDataFeedVendorRule, AdminDataFeedVendorRuleImportResult } from "../../app/models/Admin";
import { formatLocalDateTime } from "./utils/dateTime";

type AdminConsoleContext = { token: string; onUnauthorized: () => void; };
const matchModeOptions = [
  "ShopifyDirect",
  "ShopifyWithPrefix",
  "Turn14Only",
  "Turn14ThenShopify",
  "ShopifyThenTurn14",
  "ShopifyReplaceLeadingLettersWithPrefix",
  "ShopifyTrailingCodeToPrefix",
  "ShopifyTrimLeadingZerosWithPrefix",
  "Ignore"
].map((x) => ({ label: x, value: x }));
const matchModeDescriptions: { mode: string; description: string; }[] = [
  { mode: "ShopifyDirect", description: "Only try to match the feed SKU directly against Shopify SKU values." },
  { mode: "ShopifyWithPrefix", description: "Match using Shopify SKU values after applying the configured SKU prefix." },
  { mode: "Turn14Only", description: "Resolve the SKU using Turn14-backed mapping only, without Shopify fallback." },
  { mode: "Turn14ThenShopify", description: "Try Turn14-backed mapping first, then fall back to Shopify SKU matching if no Turn14 match is found." },
  { mode: "ShopifyThenTurn14", description: "Try Shopify SKU matching first, then fall back to Turn14-backed mapping if Shopify does not match." },
  { mode: "ShopifyReplaceLeadingLettersWithPrefix", description: "Replace the leading feed SKU letters with the configured Shopify SKU prefix." },
  { mode: "ShopifyTrailingCodeToPrefix", description: "Move or remove the configured trailing vendor code so it appears at the front of the Shopify SKU." },
  { mode: "ShopifyTrimLeadingZerosWithPrefix", description: "Trim numeric leading zero padding from the feed SKU, then apply the configured Shopify SKU prefix." },
  { mode: "Ignore", description: "Skip matching for this vendor rule." }
];

const getApiErrorMessage = (error: any, fallback: string) => {
  if (typeof error?.response?.data === "string" && error.response.data.trim()) {
    return error.response.data.trim();
  }

  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message.trim();
  }

  return fallback;
};

const buildNewDataFeedProfile = (shopId: number): AdminDataFeedProfile => ({
  id: 0,
  shopId,
  name: "",
  providerCode: "",
  protocol: "SFTP",
  host: "",
  port: 22,
  username: "",
  password: "",
  remotePath: "",
  filePattern: "*.csv",
  isPassive: true,
  isEnabled: false,
  treatAsFullSnapshot: true,
  vendorColumnName: "",
  defaultVendor: "",
  isSingleVendorProfile: false,
  skuColumnName: "SKU",
  quantityColumnName: "Quantity",
  delimiter: ",",
  treatBlankQuantityAsZero: true,
  quantityBooleanMode: "",
  shopifyLocationId: undefined,
  mapToLocationIfMissing: false,
  normalizeShopifySkuSpaces: false,
  stripQuantitySuffixPlus: false,
  removeSpecialCharactersFromShopifySku: false,
  syncIntervalMinutes: 15,
  lastRunAt: undefined,
  nextRunAt: undefined,
  runOnStartup: false,
  createdAt: new Date().toISOString(),
  updatedAt: undefined
});

export default function AdminDataFeedStoreDetailsPage() {
  const { token, onUnauthorized } = useOutletContext<AdminConsoleContext>();
  const { shopId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const parsedShopId = Number(shopId || "0");
  const requestedTab = searchParams.get("tab");
  const tab = requestedTab === "vendor-rules" || requestedTab === "inventory-diagnostics" ? requestedTab : "profiles";
  const selectedTab = tab === "vendor-rules" ? 1 : tab === "inventory-diagnostics" ? 2 : 0;

  const [details, setDetails] = useState<AdminDataFeedStoreDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDetails = useCallback(async () => {
    if (!Number.isFinite(parsedShopId) || parsedShopId <= 0) return;
    setLoading(true); setError(null);
    try {
      setDetails(await agent.Admin.dataFeedStoreDetails(token, parsedShopId));
    } catch {
      setError("Unable to load data feed store details.");
      onUnauthorized();
    } finally { setLoading(false); }
  }, [parsedShopId, token, onUnauthorized]);

  useEffect(() => { loadDetails(); }, [loadDetails]);

  if (!Number.isFinite(parsedShopId) || parsedShopId <= 0) return <Banner tone="critical">Invalid shop ID.</Banner>;

  return (
    <div style={{ display: "grid", gap: "0.75rem" }}>
      <Button variant="plain" onClick={() => navigate("/pf-console/data-feed?tab=stores")}>← Back to Data Feed stores</Button>
      <Card>
        <div style={{ display: "grid", gap: "0.5rem" }}>
          <Text as="h3" variant="headingMd">{details?.shopName || `Shop ${parsedShopId}`}</Text>
          <Text as="p" tone="subdued">{details?.shopDomain || "-"}</Text>
        </div>
      </Card>
      {error ? <Banner tone="critical">{error}</Banner> : null}
      {!details ? (loading ? <Card><Text as="p">Loading details...</Text></Card> : null) : (
        <>
          <Card>
            <Tabs
              tabs={[
                { id: "profiles", content: "Profile details" },
                { id: "vendor-rules", content: "Vendor rules" },
                { id: "inventory-diagnostics", content: "Inventory diagnostics" }
              ]}
              selected={selectedTab}
              onSelect={(index) => {
                const nextTab = index === 1 ? "vendor-rules" : index === 2 ? "inventory-diagnostics" : "profiles";
                setSearchParams((prev) => {
                  const nextParams = new URLSearchParams(prev);
                  nextParams.set("tab", nextTab);
                  return nextParams;
                });
              }}
            />
          </Card>
          {tab === "profiles" ? (
            <ProfileEditorCard token={token} shopId={details.shopId} profiles={details.profiles} locations={details.locations || []} onSaved={loadDetails} />
          ) : tab === "inventory-diagnostics" ? (
            <InventoryDiagnosticsCard token={token} details={details} />
          ) : (
            <VendorRulesCard token={token} details={details} onSaved={loadDetails} />
          )}
        </>
      )}
    </div>
  );
}

function InventoryDiagnosticsCard({ token, details }: { token: string; details: AdminDataFeedStoreDetails; }) {
  const profileOptions = useMemo(
    () => [{ label: "All profiles", value: "" }, ...details.profiles.map((p) => ({ label: p.name || `Profile #${p.id}`, value: String(p.id) }))],
    [details.profiles]
  );
  const [sku, setSku] = useState("");
  const [vendor, setVendor] = useState("");
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AdminDataFeedInventoryDiagnosticsResponse | null>(null);

  const runDiagnostics = async () => {
    if (!sku.trim()) {
      setError("SKU is required.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await agent.Admin.dataFeedInventoryDiagnostics(
        token,
        details.shopId,
        sku.trim(),
        vendor.trim() || undefined,
        selectedProfileId ? Number(selectedProfileId) : undefined
      );
      setResult(response);
    } catch {
      setError("Unable to run inventory diagnostics.");
    } finally {
      setLoading(false);
    }
  };

  return <Card>
    <div style={{ display: "grid", gap: "0.75rem" }}>
      <Text as="h4" variant="headingSm">Trace a SKU through data-feed inventory mapping</Text>
      <FormLayout>
        <TextField label="SKU (from CSV feed)" value={sku} onChange={setSku} autoComplete="off" />
        <TextField label="Vendor (optional, from CSV feed)" value={vendor} onChange={setVendor} autoComplete="off" />
        <Select label="Profile" options={profileOptions} value={selectedProfileId} onChange={setSelectedProfileId} />
      </FormLayout>
      <Text as="p" tone="subdued">Use feed values here. The tool applies configured prefix + normalization and then shows matched Shopify SKU candidates.</Text>
      <div>
        <Button variant="primary" onClick={runDiagnostics} loading={loading}>Run diagnostics</Button>
      </div>
      {error ? <Banner tone="critical">{error}</Banner> : null}
      {result?.notes?.length ? <Banner tone="info">{result.notes.join(" ")}</Banner> : null}
      {result ? (
        <>
          <Text as="h5" variant="headingSm">Feed rows ({result.feedRows.length})</Text>
          {result.feedRows.length === 0 ? <Text as="p">No matching feed rows.</Text> : (
            <IndexTable
              resourceName={{ singular: "feed row", plural: "feed rows" }}
              itemCount={result.feedRows.length}
              selectable={false}
              headings={[{ title: "Profile" }, { title: "Vendor" }, { title: "Qty" }, { title: "Rule" }, { title: "Candidate SKU" }, { title: "Normalized SKU" }, { title: "Matched Variants" }, { title: "Diagnosis" }]}
            >
              {result.feedRows.map((row, index) => (
                <IndexTable.Row id={`${row.externalInventoryCacheId}`} key={`${row.externalInventoryCacheId}-${index}`} position={index}>
                  <IndexTable.Cell>{row.profileName || `#${row.dataFeedProfileId}`}</IndexTable.Cell>
                  <IndexTable.Cell>{row.effectiveVendor || row.externalVendor || "-"}</IndexTable.Cell>
                  <IndexTable.Cell>{row.quantityAvailable ?? 0}</IndexTable.Cell>
                  <IndexTable.Cell>{row.matchedRuleMatchMode || "No rule"}</IndexTable.Cell>
                  <IndexTable.Cell>{row.candidateMatchSku || "-"}</IndexTable.Cell>
                  <IndexTable.Cell>{row.candidateMatchSkuNormalized || "-"}</IndexTable.Cell>
                  <IndexTable.Cell>{row.matchedVariants.map((x) => x.sku || x.variantId).join(", ") || "None"}</IndexTable.Cell>
                  <IndexTable.Cell>{row.diagnosis || "-"}</IndexTable.Cell>
                </IndexTable.Row>
              ))}
            </IndexTable>
          )}

          <Text as="h5" variant="headingSm">Recent queue updates ({result.recentShopifyUpdates.length})</Text>
          {result.recentShopifyUpdates.length === 0 ? <Text as="p">No recent queue records for this SKU.</Text> : (
            <IndexTable
              resourceName={{ singular: "queue update", plural: "queue updates" }}
              itemCount={result.recentShopifyUpdates.length}
              selectable={false}
              headings={[{ title: "ID" }, { title: "Profile" }, { title: "Variant" }, { title: "New Qty" }, { title: "Processed" }, { title: "Error" }]}
            >
              {result.recentShopifyUpdates.map((row, index) => (
                <IndexTable.Row id={`${row.id}`} key={row.id} position={index}>
                  <IndexTable.Cell>{row.id}</IndexTable.Cell>
                  <IndexTable.Cell>{row.dataFeedProfileId}</IndexTable.Cell>
                  <IndexTable.Cell>{row.shopifyVariantId}</IndexTable.Cell>
                  <IndexTable.Cell>{row.newQuantity}</IndexTable.Cell>
                  <IndexTable.Cell>{formatLocalDateTime(row.processedAt)}</IndexTable.Cell>
                  <IndexTable.Cell>{row.errorMessage || "-"}</IndexTable.Cell>
                </IndexTable.Row>
              ))}
            </IndexTable>
          )}
        </>
      ) : null}
    </div>
  </Card>;
}

function ProfileEditorCard({
  token,
  shopId,
  profiles,
  locations,
  onSaved
}: {
  token: string;
  shopId: number;
  profiles: AdminDataFeedProfile[];
  locations: AdminDataFeedShopifyLocation[];
  onSaved: () => void;
}) {
  const profileOptions = useMemo(() => profiles.map((p) => ({ label: p.name || `Profile #${p.id}`, value: String(p.id) })), [profiles]);
  const [selectedProfileId, setSelectedProfileId] = useState(profileOptions[0]?.value || "");
  const [isCreating, setIsCreating] = useState(profiles.length === 0);
  const newProfile = useMemo(() => buildNewDataFeedProfile(shopId), [shopId]);
  const selectedProfile = useMemo(
    () => profiles.find((profile) => String(profile.id) === selectedProfileId) || null,
    [profiles, selectedProfileId]
  );

  useEffect(() => {
    if (!profileOptions.length) {
      setSelectedProfileId("");
      return;
    }

    setSelectedProfileId((currentValue) => {
      if (currentValue && profileOptions.some((option) => option.value === currentValue)) {
        return currentValue;
      }

      return profileOptions[0].value;
    });
  }, [profileOptions]);

  useEffect(() => {
    if (profiles.length === 0) {
      setIsCreating(true);
    }
  }, [profiles.length]);

  return (
    <Card>
      <div style={{ display: "grid", gap: "0.75rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "end", gap: "0.75rem", flexWrap: "wrap" }}>
          {profiles.length > 0 && !isCreating ? (
            <div style={{ flex: "1 1 18rem" }}>
              <Select label="Profile" options={profileOptions} value={selectedProfileId} onChange={setSelectedProfileId} />
            </div>
          ) : (
            <Text as="h4" variant="headingSm">Add data feed profile</Text>
          )}
          <Button variant={isCreating ? undefined : "primary"} onClick={() => setIsCreating(true)} disabled={isCreating}>Add profile</Button>
        </div>
        {isCreating ? (
          <ProfileEditor
            token={token}
            profile={newProfile}
            isCreating
            onSaved={() => {
              setIsCreating(false);
              onSaved();
            }}
            onCancel={profiles.length > 0 ? () => setIsCreating(false) : undefined}
            locations={locations}
          />
        ) : selectedProfile ? (
          <ProfileEditor token={token} profile={selectedProfile} locations={locations} onSaved={onSaved} />
        ) : (
          <Text as="p">No profiles found for this store.</Text>
        )}
      </div>
    </Card>
  );
}

function buildProfilePayload(form: AdminDataFeedProfile) {
  return {
    name: form.name,
    providerCode: form.providerCode,
    protocol: form.protocol,
    host: form.host,
    port: Number(form.port) || 0,
    username: form.username,
    password: form.password,
    remotePath: form.remotePath,
    filePattern: form.filePattern,
    isPassive: form.isPassive,
    isEnabled: form.isEnabled,
    treatAsFullSnapshot: form.treatAsFullSnapshot,
    vendorColumnName: form.vendorColumnName,
    defaultVendor: form.defaultVendor,
    isSingleVendorProfile: form.isSingleVendorProfile,
    skuColumnName: form.skuColumnName,
    quantityColumnName: form.quantityColumnName,
    delimiter: form.delimiter,
    treatBlankQuantityAsZero: form.treatBlankQuantityAsZero,
    quantityBooleanMode: form.quantityBooleanMode,
    shopifyLocationId: form.shopifyLocationId,
    mapToLocationIfMissing: form.mapToLocationIfMissing,
    normalizeShopifySkuSpaces: form.normalizeShopifySkuSpaces,
    stripQuantitySuffixPlus: form.stripQuantitySuffixPlus,
    removeSpecialCharactersFromShopifySku: form.removeSpecialCharactersFromShopifySku,
    syncIntervalMinutes: Number(form.syncIntervalMinutes) || 1,
    nextRunAt: form.nextRunAt,
    runOnStartup: form.runOnStartup
  };
}

function ProfileEditor({
  token,
  profile,
  isCreating = false,
  locations = [],
  onSaved,
  onCancel
}: {
  token: string;
  profile: AdminDataFeedProfile;
  isCreating?: boolean;
  locations?: AdminDataFeedShopifyLocation[];
  onSaved: () => void;
  onCancel?: () => void;
}) {
  const [form, setForm] = useState(profile);
  const [saving, setSaving] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const [downloadingDryRun, setDownloadingDryRun] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const update = (key: keyof AdminDataFeedProfile, value: any) => setForm((p) => ({ ...p, [key]: value }));
  const locationOptions = useMemo(() => {
    const options = [
      { label: "No profile-level location", value: "" },
      ...locations.map((location) => {
        const name = location.name || `Location ${location.id}`;
        const suffixes = [
          location.isPrimary ? "Primary" : "",
          location.isActive ? "" : "Inactive"
        ].filter(Boolean);
        const suffix = suffixes.length ? ` (${suffixes.join(", ")})` : "";
        const address = location.address ? ` - ${location.address}` : "";

        return {
          label: `${name}${suffix}${address}`,
          value: String(location.id)
        };
      })
    ];

    if (form.shopifyLocationId && !locations.some((location) => location.id === form.shopifyLocationId)) {
      options.push({
        label: `Location ${form.shopifyLocationId}`,
        value: String(form.shopifyLocationId)
      });
    }

    return options;
  }, [form.shopifyLocationId, locations]);

  useEffect(() => {
    setForm(profile);
    setError(null);
  }, [profile]);

  const persistProfile = async () => {
    if (isCreating && !form.name?.trim()) {
      setError("Name is required.");
      return false;
    }

    const payload = buildProfilePayload(form);
    if (isCreating) {
      await agent.Admin.createDataFeedProfile(token, profile.shopId, payload);
    } else {
      await agent.Admin.updateDataFeedProfile(token, profile.id, payload);
    }

    return true;
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = await persistProfile();
      if (!saved) return;
      onSaved();
    } catch (error: any) {
      setError(getApiErrorMessage(error, isCreating ? "Unable to create data feed profile." : "Unable to save profile settings."));
    } finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunningNow(true);
    setError(null);
    try {
      await agent.Admin.runDataFeedProfileNow(token, profile.id);
      onSaved();
    } catch (error: any) {
      setError(getApiErrorMessage(error, "Unable to run data feed profile."));
    } finally {
      setRunningNow(false);
    }
  };

  const downloadDryRunMatches = async () => {
    if (!form.shopifyLocationId) {
      setError("Select a Shopify Location before downloading a mock run CSV.");
      return;
    }

    setDownloadingDryRun(true);
    setError(null);
    try {
      const saved = await persistProfile();
      if (!saved) return;

      const response = await agent.Admin.downloadDataFeedProfileDryRunMatchesCsv(token, profile.id);
      const blobUrl = window.URL.createObjectURL(response.data);
      const safeProfileName = (profile.name || `profile-${profile.id}`).replace(/[^a-zA-Z0-9-_]/g, "_");
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `data-feed-mock-run-${safeProfileName}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      onSaved();
    } catch {
      setError("Unable to download mock run CSV.");
    } finally {
      setDownloadingDryRun(false);
    }
  };

  return <div style={{ display: "grid", gap: "0.75rem" }}>
    <Text as="h4" variant="headingSm">{isCreating ? "New profile" : `Profile: ${profile.name || `#${profile.id}`}`}</Text>
    {error ? <Banner tone="critical">{error}</Banner> : null}
    <FormLayout>
      <TextField label="Name" value={form.name || ""} onChange={(v) => update("name", v)} autoComplete="off" />
      <TextField label="Provider Code" value={form.providerCode || ""} onChange={(v) => update("providerCode", v)} autoComplete="off" />
      <TextField label="Protocol" value={form.protocol || ""} onChange={(v) => update("protocol", v)} autoComplete="off" />
      <TextField label="Host" value={form.host || ""} onChange={(v) => update("host", v)} autoComplete="off" />
      <TextField label="Port" type="number" value={String(form.port || 0)} onChange={(v) => update("port", Number(v) || 0)} autoComplete="off" />
      <TextField label="Username" value={form.username || ""} onChange={(v) => update("username", v)} autoComplete="off" />
      <TextField label="Password" value={form.password || ""} onChange={(v) => update("password", v)} autoComplete="off" />
      <TextField label="Remote Path" value={form.remotePath || ""} onChange={(v) => update("remotePath", v)} autoComplete="off" />
      <TextField label="File Pattern" value={form.filePattern || ""} onChange={(v) => update("filePattern", v)} autoComplete="off" />
      <Checkbox label="Passive FTP/FTPS mode" checked={form.isPassive} onChange={(v) => update("isPassive", v)} />
      <TextField label="Vendor Column Name" value={form.vendorColumnName || ""} onChange={(v) => update("vendorColumnName", v)} autoComplete="off" />
      <TextField label="SKU Column Name" value={form.skuColumnName || ""} onChange={(v) => update("skuColumnName", v)} autoComplete="off" />
      <TextField label="Quantity Column Name" value={form.quantityColumnName || ""} onChange={(v) => update("quantityColumnName", v)} autoComplete="off" />
      <TextField
        label="Quantity Boolean Mode"
        value={form.quantityBooleanMode || ""}
        onChange={(v) => update("quantityBooleanMode", v)}
        autoComplete="off"
      />
      <TextField label="Default Vendor" value={form.defaultVendor || ""} onChange={(v) => update("defaultVendor", v)} autoComplete="off" />
      <TextField label="Delimiter" value={form.delimiter || ","} onChange={(v) => update("delimiter", v)} autoComplete="off" />
      <TextField label="Sync Interval (minutes)" type="number" value={String(form.syncIntervalMinutes || 1)} onChange={(v) => update("syncIntervalMinutes", Number(v) || 1)} autoComplete="off" />
      {locations.length > 0 ? (
        <Select
          label="Shopify Location"
          options={locationOptions}
          value={form.shopifyLocationId ? String(form.shopifyLocationId) : ""}
          onChange={(value) => update("shopifyLocationId", value ? Number(value) : undefined)}
        />
      ) : (
        <TextField
          label="Shopify Location ID"
          type="number"
          value={form.shopifyLocationId ? String(form.shopifyLocationId) : ""}
          onChange={(value) => update("shopifyLocationId", value ? Number(value) : undefined)}
          autoComplete="off"
        />
      )}
      <Checkbox label="Enabled" checked={form.isEnabled} onChange={(v) => update("isEnabled", v)} />
      <Checkbox label="Treat as full snapshot" checked={form.treatAsFullSnapshot} onChange={(v) => update("treatAsFullSnapshot", v)} />
      <Checkbox label="Treat blank quantity as zero" checked={form.treatBlankQuantityAsZero} onChange={(v) => update("treatBlankQuantityAsZero", v)} />
      <Checkbox label="Single vendor profile" checked={form.isSingleVendorProfile} onChange={(v) => update("isSingleVendorProfile", v)} />
      <Checkbox label="Map to location if missing" checked={form.mapToLocationIfMissing} onChange={(v) => update("mapToLocationIfMissing", v)} />
      <Checkbox label="Run on startup" checked={form.runOnStartup} onChange={(v) => update("runOnStartup", v)} />
      <Checkbox
        label="Normalize Shopify SKU for matching (remove spaces only)"
        checked={form.normalizeShopifySkuSpaces}
        onChange={(v) => update("normalizeShopifySkuSpaces", v)}
      />
      <Checkbox
        label="Strip '+' from quantity values (e.g., 4+ => 4)"
        checked={form.stripQuantitySuffixPlus}
        onChange={(v) => update("stripQuantitySuffixPlus", v)}
      />
      <Checkbox
        label="Remove special characters from Shopify SKU for matching"
        checked={form.removeSpecialCharactersFromShopifySku}
        onChange={(v) => update("removeSpecialCharactersFromShopifySku", v)}
      />
    </FormLayout>
    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
      <Button variant="primary" onClick={save} loading={saving}>{isCreating ? "Create profile" : "Save profile settings"}</Button>
      {!isCreating ? <Button onClick={runNow} loading={runningNow}>Run data feed pull now</Button> : null}
      {!isCreating ? <Button onClick={downloadDryRunMatches} loading={downloadingDryRun} disabled={!form.shopifyLocationId || saving}>Download mock run CSV</Button> : null}
      {onCancel ? <Button onClick={onCancel}>Cancel</Button> : null}
    </div>
    {!isCreating && !form.shopifyLocationId ? <Text as="p" tone="subdued">Select a Shopify Location before downloading a mock run CSV.</Text> : null}
  </div>;
}

function VendorRulesCard({ token, details, onSaved }: { token: string; details: AdminDataFeedStoreDetails; onSaved: () => void; }) {
  const profileOptions = useMemo(() => details.profiles.map((p) => ({ label: p.name || `Profile #${p.id}`, value: String(p.id) })), [details.profiles]);
  const [selectedProfileId, setSelectedProfileId] = useState(profileOptions[0]?.value || "");
  const [showMatchModeHelp, setShowMatchModeHelp] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<AdminDataFeedVendorRuleImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const filteredRules = useMemo(
    () => details.vendorRules.filter((rule) => String(rule.dataFeedProfileId) === selectedProfileId),
    [details.vendorRules, selectedProfileId]
  );
  const [newRule, setNewRule] = useState({
    dataFeedProfileId: profileOptions[0]?.value || "",
    shopifyVendor: "",
    feedVendor: "",
    shopifySkuPrefix: "",
    matchMode: "ShopifyDirect"
  });

  const saveRule = async (rule: AdminDataFeedVendorRule) => {
    await agent.Admin.updateDataFeedVendorRule(token, rule.id, {
      shopifyVendor: rule.shopifyVendor,
      feedVendor: rule.feedVendor,
      matchMode: rule.matchMode,
      shopifySkuPrefix: rule.shopifySkuPrefix,
      shopifyLocationId: rule.shopifyLocationId,
      mapToLocationIfMissing: rule.mapToLocationIfMissing,
      isTurn14Backed: rule.isTurn14Backed
    });
    onSaved();
  };

  const addRule = async () => {
    if (!selectedProfileId) return;
    await agent.Admin.createDataFeedVendorRule(token, Number(selectedProfileId), {
      shopifyVendor: newRule.shopifyVendor,
      feedVendor: newRule.feedVendor,
      shopifySkuPrefix: newRule.shopifySkuPrefix,
      matchMode: newRule.matchMode,
      mapToLocationIfMissing: false,
      isTurn14Backed: false
    });
    setNewRule((r) => ({ ...r, shopifyVendor: "", feedVendor: "", shopifySkuPrefix: "" }));
    onSaved();
  };

  useEffect(() => {
    if (!profileOptions.length) {
      setSelectedProfileId("");
      setNewRule((rule) => ({ ...rule, dataFeedProfileId: "" }));
      return;
    }

    setSelectedProfileId((currentValue) => {
      if (currentValue && profileOptions.some((option) => option.value === currentValue)) {
        return currentValue;
      }

      return profileOptions[0].value;
    });
  }, [profileOptions]);

  useEffect(() => {
    setNewRule((rule) => ({ ...rule, dataFeedProfileId: selectedProfileId }));
    setImportResult(null);
    setImportError(null);
  }, [selectedProfileId]);

  const exportRulesToCsv = useCallback(() => {
    if (!filteredRules.length) return;

    const selectedProfileLabel = profileOptions.find((option) => option.value === selectedProfileId)?.label || `profile-${selectedProfileId || "unknown"}`;
    const csvEscape = (value: string) => `"${value.replace(/"/g, "\"\"")}"`;
    const headers = ["Shopify Vendor", "Feed Vendor", "SKU Prefix", "Match Mode"];
    const rows = filteredRules.map((rule) => [
      rule.shopifyVendor || "",
      rule.feedVendor || "",
      rule.shopifySkuPrefix || "",
      rule.matchMode || ""
    ]);

    const csvText = [headers, ...rows]
      .map((row) => row.map((column) => csvEscape(column)).join(","))
      .join("\n");

    const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
    const fileName = `vendor-rules-${selectedProfileLabel.replace(/[^a-zA-Z0-9-_]/g, "_")}.csv`;
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [filteredRules, profileOptions, selectedProfileId]);

  const importRulesFromCsv = useCallback(async (file: File) => {
    if (!selectedProfileId) return;

    setImporting(true);
    setImportResult(null);
    setImportError(null);
    try {
      const result = await agent.Admin.importDataFeedVendorRules(token, Number(selectedProfileId), file);
      setImportResult(result);
      onSaved();
    } catch (error: any) {
      setImportError(getApiErrorMessage(error, "Unable to import selected profile rules."));
    } finally {
      setImporting(false);
    }
  }, [onSaved, selectedProfileId, token]);

  const handleImportFileChange = useCallback((event: any) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (file) {
      importRulesFromCsv(file);
    }
  }, [importRulesFromCsv]);

  return <Card>
    <div style={{ display: "grid", gap: "0.75rem" }}>
      <Text as="h4" variant="headingSm">Vendor feed mapping rules</Text>
      <Select label="Profile" options={profileOptions} value={selectedProfileId} onChange={setSelectedProfileId} />
      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
        <Button onClick={exportRulesToCsv} disabled={!filteredRules.length}>Export selected profile rules (CSV)</Button>
        <Button onClick={() => fileInputRef.current?.click()} loading={importing} disabled={!selectedProfileId}>Import selected profile rules (CSV)</Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: "none" }}
          onChange={handleImportFileChange}
        />
      </div>
      {importError ? <Banner tone="critical">{importError}</Banner> : null}
      {importResult ? (
        <Banner tone={importResult.skippedCount > 0 ? "warning" : "success"}>
          <div style={{ display: "grid", gap: "0.25rem" }}>
            <Text as="p">
              Imported {importResult.totalRows.toLocaleString()} row(s). Created {importResult.createdCount.toLocaleString()}, updated {importResult.updatedCount.toLocaleString()}, skipped {importResult.skippedCount.toLocaleString()}.
            </Text>
            {importResult.errors.slice(0, 5).map((message) => (
              <Text as="p" key={message}>{message}</Text>
            ))}
          </div>
        </Banner>
      ) : null}
      <IndexTable resourceName={{ singular: "vendor rule", plural: "vendor rules" }} itemCount={filteredRules.length} selectable={false}
        headings={[
          { title: "Profile" },
          { title: "Shopify Vendor" },
          { title: "Feed Vendor" },
          { title: "SKU Prefix" },
          {
            id: "match-mode-help",
            title: (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                Match Mode
                <Button
                  variant="plain"
                  onClick={() => setShowMatchModeHelp((value) => !value)}
                  accessibilityLabel="Show match mode explanations"
                  ariaExpanded={showMatchModeHelp}
                  icon={QuestionCircleIcon}
                />
              </span>
            )
          },
          { title: "Actions" }
        ]}>
        {filteredRules.map((rule, idx) => (
          <EditableRuleRow key={rule.id} rule={rule} profiles={details.profiles} onSave={saveRule} onDelete={async () => { await agent.Admin.deleteDataFeedVendorRule(token, rule.id); onSaved(); }} index={idx} />
        ))}
      </IndexTable>
      {showMatchModeHelp ? (
        <Banner tone="info">
          <div style={{ display: "grid", gap: "0.25rem" }}>
            {matchModeDescriptions.map((item) => (
              <Text as="p" key={item.mode}>
                <strong>{item.mode}:</strong> {item.description}
              </Text>
            ))}
            <div style={{ height: "0.25rem" }} />
            <Text as="p"><strong>How Turn14 matching currently works:</strong></Text>
            <Text as="p">Take the feed row SKU (<code>ExternalSkuTrimmed</code>).</Text>
            <Text as="p">Find a Turn14 product where:</Text>
            <Text as="p">• <code>Products.MfrPartNumber == feed SKU</code></Text>
            <Text as="p">• <code>Products.Brand == rule.ShopifyVendor</code> (normalized/lowercased compare)</Text>
            <Text as="p">• product is linked to the shop&rsquo;s <code>Turn14AccountId</code> via <code>AccountProducts</code>.</Text>
            <Text as="p">Use Turn14 <code>PartNumber</code> as the SKU to match Shopify variants.</Text>
            <Text as="p">If no Turn14 mapping is found, it falls back to prefixed/direct feed SKU logic.</Text>
            <Text as="p">(So it is not strictly &ldquo;Turn14 only&rdquo; in this stored procedure path.)</Text>
          </div>
        </Banner>
      ) : null}
      <FormLayout>
        <TextField label="Shopify Vendor" value={newRule.shopifyVendor} onChange={(v) => setNewRule((r) => ({ ...r, shopifyVendor: v }))} autoComplete="off" />
        <TextField label="Feed Vendor" value={newRule.feedVendor} onChange={(v) => setNewRule((r) => ({ ...r, feedVendor: v }))} autoComplete="off" />
        <TextField label="SKU Prefix" value={newRule.shopifySkuPrefix} onChange={(v) => setNewRule((r) => ({ ...r, shopifySkuPrefix: v }))} autoComplete="off" />
        <Select label="Match Mode" options={matchModeOptions} value={newRule.matchMode} onChange={(v) => setNewRule((r) => ({ ...r, matchMode: v }))} />
        <Button variant="primary" onClick={addRule}>Add vendor rule</Button>
      </FormLayout>
    </div>
  </Card>;
}

function EditableRuleRow({ rule, profiles, onSave, onDelete, index }: any) {
  const [form, setForm] = useState<AdminDataFeedVendorRule>(rule);
  useEffect(() => setForm(rule), [rule]);
  const profileName = profiles.find((p: AdminDataFeedProfile) => p.id === form.dataFeedProfileId)?.name || `#${form.dataFeedProfileId}`;
  return <IndexTable.Row id={`${form.id}`} position={index}>
    <IndexTable.Cell>{profileName}</IndexTable.Cell>
    <IndexTable.Cell><TextField label="Shopify Vendor" labelHidden value={form.shopifyVendor || ""} onChange={(v) => setForm((x) => ({ ...x, shopifyVendor: v }))} autoComplete="off" /></IndexTable.Cell>
    <IndexTable.Cell><TextField label="Feed Vendor" labelHidden value={form.feedVendor || ""} onChange={(v) => setForm((x) => ({ ...x, feedVendor: v }))} autoComplete="off" /></IndexTable.Cell>
    <IndexTable.Cell><TextField label="SKU Prefix" labelHidden value={form.shopifySkuPrefix || ""} onChange={(v) => setForm((x) => ({ ...x, shopifySkuPrefix: v }))} autoComplete="off" /></IndexTable.Cell>
    <IndexTable.Cell><Select label="Match Mode" labelHidden options={matchModeOptions} value={form.matchMode || "ShopifyDirect"} onChange={(v) => setForm((x) => ({ ...x, matchMode: v }))} /></IndexTable.Cell>
    <IndexTable.Cell><div style={{ display: "flex", gap: "0.5rem" }}><Button onClick={() => onSave(form)}>Save</Button><Button tone="critical" onClick={onDelete}>Delete</Button></div></IndexTable.Cell>
  </IndexTable.Row>;
}
